import { isRequestError } from 'utils/requestError';
import './FinancialLimits.scss';

import { Card, InputSelect, Loading, ModalPinCode } from 'components';
import { numberFormat } from 'configs';
import { Button, useForm } from 'modules';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useCheckPinCodeMutation,
  useGetFinancialLimitOptionsQuery,
  useGetUserProfileQuery,
  useUpdateOwnFinancialLimitsMutation,
} from 'services';
import type { UpdateOwnFinancialLimitDto } from 'services/openapi';
import * as Yup from 'yup';

type FinancialLimitField =
  | 'perTransactionLimit'
  | 'dailyLimit'
  | 'monthlyLimit';

const limitLabels: Record<FinancialLimitField, string> = {
  perTransactionLimit: 'Giới hạn mỗi giao dịch',
  dailyLimit: 'Giới hạn trong ngày',
  monthlyLimit: 'Giới hạn trong tháng',
};

const limitFields: FinancialLimitField[] = [
  'perTransactionLimit',
  'dailyLimit',
  'monthlyLimit',
];

const formatLimit = (value?: number) =>
  typeof value === 'number' ? `${numberFormat.format(value)} VNĐ` : 'Chưa có';

const toLimitOptions = (values?: number[]) =>
  (values || []).map((value) => ({
    label: formatLimit(value),
    value,
  }));

const PersonalFinancialLimitsFeature = () => {
  const [isShowPinPopup, setIsShowPinPopup] = useState(false);
  const [pendingPayload, setPendingPayload] =
    useState<UpdateOwnFinancialLimitDto>();
  const { data: profile, isLoading: isLoadingProfile } =
    useGetUserProfileQuery();
  const { data: dataOptions, isLoading: isLoadingOptions } =
    useGetFinancialLimitOptionsQuery();
  const {
    mutateAsync: updateOwnFinancialLimits,
    isLoading: isUpdatingOwnFinancialLimits,
  } = useUpdateOwnFinancialLimitsMutation();
  const {
    mutateAsync: mutateAsyncCheckPinCode,
    isLoading: isLoadingCheckPinCode,
  } = useCheckPinCodeMutation();

  const options = dataOptions?.data;

  const perTransactionLimitOptions = useMemo(
    () => toLimitOptions(options?.perTransactionLimits),
    [options?.perTransactionLimits],
  );
  const dailyLimitOptions = useMemo(
    () => toLimitOptions(options?.dailyLimits),
    [options?.dailyLimits],
  );
  const monthlyLimitOptions = useMemo(
    () => toLimitOptions(options?.monthlyLimits),
    [options?.monthlyLimits],
  );

  const optionsByField: Record<FinancialLimitField, any[]> = {
    perTransactionLimit: perTransactionLimitOptions,
    dailyLimit: dailyLimitOptions,
    monthlyLimit: monthlyLimitOptions,
  };

  const { handleSubmit, getInputProps, isSubmitting } = useForm({
    enableReinitialize: true,
    structure: [
      {
        name: 'perTransactionLimit',
        defaultValue: profile?.perTransactionLimit || '',
        validate: Yup.number().required('Vui lòng chọn hạn mức mỗi giao dịch'),
      },
      {
        name: 'dailyLimit',
        defaultValue: profile?.dailyLimit || '',
        validate: Yup.number().required('Vui lòng chọn hạn mức trong ngày'),
      },
      {
        name: 'monthlyLimit',
        defaultValue: profile?.monthlyLimit || '',
        validate: Yup.number().required('Vui lòng chọn hạn mức trong tháng'),
      },
    ],
    onSubmit: async (values) => {
      setPendingPayload({
        perTransactionLimit: values.perTransactionLimit,
        dailyLimit: values.dailyLimit,
        monthlyLimit: values.monthlyLimit,
      });
      setIsShowPinPopup(true);
    },
  });

  const handleConfirmPinCode = async (pinCode: string) => {
    if (!pendingPayload) {
      setIsShowPinPopup(false);
      return;
    }

    try {
      const dataCheckPinCode = await mutateAsyncCheckPinCode({ pinCode });

      if (!dataCheckPinCode?.isValid) {
        dataCheckPinCode?.message?.map((item: string) => toast.error(item));
        return;
      }

      await updateOwnFinancialLimits(pendingPayload);
      toast.success('Cập nhật hạn mức thành công');
      setPendingPayload(undefined);
      setIsShowPinPopup(false);
    } catch (error: any) {
      if (!isRequestError(error)) {
        toast.error(error?.message || 'Cập nhật hạn mức thất bại');
      }
    }
  };

  const isBusy =
    isSubmitting ||
    isUpdatingOwnFinancialLimits ||
    isLoadingCheckPinCode ||
    isLoadingProfile ||
    isLoadingOptions;

  return (
    <>
      <Card className='container-fluid financial-limits'>
        <div className='financial-limits__content'>
          <div className='financial-limits__header'>
            <h2 className='financial-limits__title'>Quản lý hạn mức</h2>
          </div>

          <Loading isLoading={isLoadingProfile || isLoadingOptions}>
            <form className='financial-limits__form' onSubmit={handleSubmit}>
              <div className='financial-limits__list'>
                {limitFields.map((field) => (
                  <div key={field} className='financial-limits__field'>
                    <div className='financial-limits__label'>
                      {limitLabels[field]}
                    </div>
                    <div className='financial-limits__control'>
                      <InputSelect
                        {...getInputProps(field)}
                        options={optionsByField[field]}
                        isClear={false}
                        isDisabled={isBusy}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className='financial-limits__actions'>
                <Button
                  label='Cập nhật'
                  buttonType='success'
                  type='submit'
                  isLoading={isUpdatingOwnFinancialLimits}
                  disabled={isBusy}
                />
              </div>
            </form>
          </Loading>
        </div>
      </Card>

      <ModalPinCode
        isOpen={isShowPinPopup}
        isLoading={isUpdatingOwnFinancialLimits || isLoadingCheckPinCode}
        onClose={() => setIsShowPinPopup(false)}
        onConfirm={handleConfirmPinCode}
      />
    </>
  );
};

export default PersonalFinancialLimitsFeature;
